import { SubtitleListBox, ListBox, SettingsPanel, SettingsPanelPage, SettingsPanelPageOpenButton, SettingsPanelPageBackButton, SettingsToggleButton, Button, Label, SettingsPanelItem, AudioQualitySelectBox, AudioTrackSelectBox } from 'bitmovin-player-ui';
import { localizeKey } from '../../js/utilities/utilities.locale';
import { handleSubtitleListBoxItemSelected, handleSpeedListBoxItemSelected, handleQualityListBoxItemSelected } from '../handlers/listboxhandlers';
import { PlayerEvent } from 'bitmovin-player';
const createListBox = (items) => new ListBox({ items });
const createPanelPage = (components, cssClasses) => new SettingsPanelPage({ components, cssClasses });
const createSettingsPanelPageBackButton = (targetPage, container, text, cssClasses) => new SettingsPanelPageBackButton({
    targetPage,
    container,
    text,
    cssClasses
});
const createSettingsOpenButton = (targetPage, container, text, cssClasses) => new SettingsPanelPageOpenButton({
    targetPage,
    container,
    text,
    cssClasses
});
export function createSettingsPanel(npoPlayer) {
    const player = 'player' in npoPlayer ? npoPlayer.player : npoPlayer;
    const subtitleListBox = new SubtitleListBox();
    const speedListBox = createListBox([
        { key: '0.25', label: '0.25x' },
        { key: '0.5', label: '0.5x' },
        { key: '1', label: '1x' },
        { key: '1.5', label: '1.5x' },
        { key: '2', label: '2x' }
    ]);
    const qualityListBox = new ListBox();
    const audioqualityLabel = localizeKey('settings.audio.quality');
    const audiotrackLabel = localizeKey('settings.audio.track');
    const autoLabel = localizeKey('auto');
    const offLabel = localizeKey('off');
    const settingsLabel = localizeKey('settings');
    const speedLabel = localizeKey('speed');
    const subtitlesLabel = localizeKey('settings.subtitles');
    const videoqualityLabel = localizeKey('settings.video.quality');
    const settingTriggerClass = 'settings-trigger';
    const settingsBackButtonClass = 'settings-back-button';
    const listboxPanelClass = 'listbox-panel';
    const listboxPagerButtonClass = 'listbox-pager-button';
    const subtitlePanelPage = createPanelPage([new Button({ cssClass: settingTriggerClass }), new SettingsPanelItem('', subtitleListBox)], [listboxPanelClass]);
    const speedPanelPage = createPanelPage([new Button({ cssClass: settingTriggerClass }), new SettingsPanelItem('', speedListBox)], [listboxPanelClass]);
    const qualityPanelPage = createPanelPage([new Button({ cssClass: settingTriggerClass }), new SettingsPanelItem('', qualityListBox)], [listboxPanelClass]);
    const settingsTriggerButton = new Button({ cssClass: settingTriggerClass });
    const mainSettingsPage = createPanelPage([
        settingsTriggerButton,
        new Label({ text: settingsLabel, cssClass: 'setting-header' }),
        new SettingsPanelItem(audioqualityLabel, new AudioQualitySelectBox()),
        new SettingsPanelItem(audiotrackLabel, new AudioTrackSelectBox())
    ], ['main-panel']);
    const onSourceLoaded = () => {
        player?.off(PlayerEvent.SourceLoaded, onSourceLoaded);
        checkQualities();
        checkSubtitles();
    };
    const settingsPanel = new SettingsPanel({
        components: [mainSettingsPage, subtitlePanelPage, speedPanelPage, qualityPanelPage],
        hidden: true,
        pageTransitionAnimation: false,
        hideDelay: -1
    });
    const subtitleSettingsOpenButton = createSettingsOpenButton(subtitlePanelPage, settingsPanel, offLabel, [
        listboxPagerButtonClass
    ]);
    const speedSettingsOpenButton = createSettingsOpenButton(speedPanelPage, settingsPanel, '1x', [
        listboxPagerButtonClass
    ]);
    const qualitySettingsOpenButton = createSettingsOpenButton(qualityPanelPage, settingsPanel, autoLabel, [
        listboxPagerButtonClass
    ]);
    const subtitlesPanelItem = new SettingsPanelItem(subtitlesLabel, subtitleSettingsOpenButton, { hidden: false });
    const speedPanelItem = new SettingsPanelItem(speedLabel, speedSettingsOpenButton);
    const qualityPanelItem = new SettingsPanelItem(videoqualityLabel, qualitySettingsOpenButton, { hidden: false });
    for (const item of [
        subtitlesPanelItem,
        speedPanelItem,
        qualityPanelItem,
        new SettingsToggleButton({ settingsPanel })
    ])
        mainSettingsPage.addComponent(item);
    for (const [index, panelPage] of [subtitlePanelPage, speedPanelPage, qualityPanelPage].entries()) {
        const labels = [subtitlesLabel, speedLabel, videoqualityLabel];
        panelPage.addComponent(createSettingsPanelPageBackButton(mainSettingsPage, settingsPanel, labels[index], [
            settingsBackButtonClass
        ]));
        panelPage.addComponent(new SettingsToggleButton({ settingsPanel }));
    }
    if ('uiComponents' in npoPlayer) {
        npoPlayer.uiComponents.subtitlesButton = subtitlesPanelItem;
        npoPlayer.uiComponents.qualityButton = qualityPanelItem;
    }
    subtitleListBox.onItemSelected.subscribe(handleSubtitleListBoxItemSelected(subtitleSettingsOpenButton, settingsPanel, mainSettingsPage));
    if (player) {
        speedListBox.selectItem('1');
        speedListBox.onItemSelected.subscribe(handleSpeedListBoxItemSelected(player, speedSettingsOpenButton, settingsPanel, mainSettingsPage));
        qualityListBox.selectItem('auto');
        qualityListBox.onItemSelected.subscribe(handleQualityListBoxItemSelected(player, qualitySettingsOpenButton, settingsPanel, mainSettingsPage));
    }
    if (npoPlayer.uiComponents) {
        const uiComponents = npoPlayer.uiComponents;
        uiComponents.settingsPanels = uiComponents.settingsPanels
            ? [...uiComponents.settingsPanels, settingsPanel]
            : [settingsPanel];
    }
    const checkSubtitles = () => {
        const subtitles = player?.subtitles?.list();
        if (subtitles && subtitles.length > 0) {
            subtitlesPanelItem.show();
        }
        else {
            subtitlesPanelItem.hide();
        }
    };
    const checkQualities = () => {
        const availableVideoQualities = player?.getAvailableVideoQualities();
        qualityListBox.clearItems();
        if (availableVideoQualities?.length) {
            for (const quality of availableVideoQualities)
                qualityListBox.addItem(quality.id, quality.label ?? 'test');
            qualityListBox.addItem('auto', autoLabel);
            qualityListBox.selectItem('auto');
            qualityPanelItem.show();
        }
        else {
            qualityPanelItem.hide();
        }
    };
    player?.on(PlayerEvent.SourceLoaded, onSourceLoaded);
    settingsPanel.onHide.subscribe(() => {
        player?.getContainer().querySelector('.bmpui-npo-player')?.classList.remove('bmpui-settings-panel-shown');
    });
    settingsPanel.onShow.subscribe(() => {
        player?.getContainer().querySelector('.bmpui-npo-player')?.classList.add('bmpui-settings-panel-shown');
    });
    return settingsPanel;
}
